import type { AiEmployeeConsoleLaunchConfig } from '@/api/modules/ai-employee-console'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { resolveAiEmployeeConsoleSubPath } from '@/utils/aiEmployeeConsoleRoute'
import eventBus from '@/utils/eventBus'

export const AI_EMPLOYEE_CONSOLE_APP_NAME = 'ai-employee-console'
export const AI_EMPLOYEE_CONSOLE_RESET_EVENT = 'ai-employee-console:reset'
export const AI_EMPLOYEE_CONSOLE_SESSION_CHANGED_EVENT = 'host:session-changed'

export interface AiEmployeeConsoleNavigationItem {
  key: string
  path: string
  title: string
  icon: string
}

const ICON_MAP: Record<string, string> = {
  'lucide:wrench': 'i-ant-design:tool-outlined',
  'lucide:settings': 'i-ant-design:setting-outlined',
  'lucide:file-pen-line': 'i-ant-design:form-outlined',
  'lucide:calendar-clock': 'i-ant-design:schedule-outlined',
  'lucide:logs': 'i-ant-design:profile-outlined',
  'lucide:receipt-text': 'i-ant-design:account-book-outlined',
  'lucide:book-open': 'i-ant-design:book-outlined',
  'lucide:messages-square': 'i-ant-design:message-outlined',
}

export function createAiEmployeeConsoleStore(id: string, prefix: string, label: string, resetEvent: string) {
  return defineStore(id, () => {
    const navigation = ref<AiEmployeeConsoleNavigationItem[]>([])
    const launchConfig = ref<AiEmployeeConsoleLaunchConfig | null>(null)
    const loading = ref(false)
    const resolved = ref(false)
    const error = ref('')
    let requestVersion = 0
    let pendingRequest: Promise<AiEmployeeConsoleLaunchConfig> | null = null

    function setNavigation(items: AiEmployeeConsoleNavigationItem[]) {
      const seen = new Set<string>()
      navigation.value = items.filter((item) => {
        const valid = /^[a-z0-9][a-z0-9-]*$/.test(item.key)
          && /^\/[a-z0-9][a-z0-9/-]*$/.test(item.path)
          && !item.path.includes('..')
          && typeof item.title === 'string'
          && item.title.trim().length > 0
          && item.title.length <= 32
          && ICON_MAP[item.icon] !== undefined
          && !seen.has(item.key)
        if (valid) {
          seen.add(item.key)
        }
        return valid
      })
      resolved.value = true
    }

    async function preload(
      loader: () => Promise<{ data: AiEmployeeConsoleLaunchConfig }>,
      force = false,
    ): Promise<AiEmployeeConsoleLaunchConfig> {
      if (!force && launchConfig.value) {
        return launchConfig.value
      }
      if (pendingRequest) {
        return pendingRequest
      }
      const version = requestVersion
      loading.value = true
      error.value = ''
      const request = loader().then((response) => {
        if (version !== requestVersion) {
          throw new Error('AI员工菜单请求已失效')
        }
        launchConfig.value = response.data
        setNavigation(response.data.bootstrap.navigation || [])
        return response.data
      }).catch((cause) => {
        if (version === requestVersion) {
          error.value = cause instanceof Error ? cause.message : 'AI员工菜单加载失败'
          resolved.value = true
        }
        throw cause
      }).finally(() => {
        if (version === requestVersion) {
          loading.value = false
        }
        if (pendingRequest === request) {
          pendingRequest = null
        }
      })
      pendingRequest = request
      return request
    }

    function injectNavigation<T extends Array<any>>(menus: T): T {
      const walk = (items: Array<any>) => {
        for (const [index, item] of items.entries()) {
          if (item.path === prefix) {
            if (error.value) {
              item.meta = { ...item.meta, title: `${label}菜单加载失败` }
            }
            else if (!resolved.value) {
              item.meta = { ...item.meta, title: `${label}菜单加载中` }
            }
            else {
              const navigationMenus = navigation.value.map(nav => ({
                path: `${prefix}${nav.path}`,
                meta: { title: nav.title, icon: ICON_MAP[nav.icon] },
              }))
              items.splice(index, 1, ...navigationMenus)
            }
            return
          }
          if (Array.isArray(item.children)) {
            walk(item.children)
          }
        }
      }
      walk(menus)
      return menus
    }

    function resolveNavigation(hostPath: string): AiEmployeeConsoleNavigationItem | undefined {
      const consolePath = resolveAiEmployeeConsoleSubPath(hostPath, '/settings', prefix)
      if (!consolePath) {
        return undefined
      }
      const item = [...navigation.value]
        .sort((left, right) => right.path.length - left.path.length)
        .find(nav => consolePath === nav.path || consolePath.startsWith(`${nav.path}/`))

      return item
        ? { ...item, icon: ICON_MAP[item.icon] }
        : undefined
    }

    function resolveHostPath(consolePath: unknown): string | undefined {
    // 忽略子应用初始化期间的根路径和未授权页面，避免覆盖宿主当前菜单。
      if (typeof consolePath !== 'string' || !/^\/[a-z0-9][a-z0-9/-]*$/.test(consolePath) || consolePath.includes('//')) {
        return undefined
      }
      const normalizedPath = consolePath === '/balance' ? '/payroll-bills' : consolePath
      const hostPath = `${prefix}${normalizedPath}`
      return resolveNavigation(hostPath) ? hostPath : undefined
    }

    function reset() {
      requestVersion += 1
      pendingRequest = null
      navigation.value = []
      launchConfig.value = null
      loading.value = false
      resolved.value = false
      error.value = ''
      eventBus.emit(resetEvent)
    }

    return { navigation, launchConfig, loading, resolved, error, preload, setNavigation, injectNavigation, resolveNavigation, resolveHostPath, reset }
  })
}

export const useAiEmployeeConsoleStore = createAiEmployeeConsoleStore(
  'aiEmployeeConsole',
  '/crae-ai-employee',
  'AI员工',
  AI_EMPLOYEE_CONSOLE_RESET_EVENT,
)
